import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { patientsService } from '@/services/patients';
import { prescriptionsService } from '@/services/prescriptions';
import { getApiError } from '@/services/api';
import { patientIdentifier } from '@/lib/patient';
import type { PrescriptionWithRelations } from '@dpfs/shared';
import { ChevronRight, FileText, ScanLine, Search, UserRound, X } from 'lucide-react';
import { PageHeader, StatusBadge, EmptyState, Spinner } from '@/components/ui';

const isDeliverable = (p: PrescriptionWithRelations) =>
  p.status === 'ACTIVE' || p.status === 'PARTIALLY_FULFILLED';

export function PrescriptionSearch() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'ar' ? 'ar-DZ' : 'fr-DZ';
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [selectedPatientName, setSelectedPatientName] = useState<string | null>(null);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(handle);
  }, [search]);

  const showResults = debouncedSearch.length >= 2 && search.trim().length >= 2;

  // Patient search
  const {
    data: patientResults,
    isFetching: patientsLoading,
    isError: patientsError,
  } = useQuery({
    queryKey: ['patients', 'search', debouncedSearch],
    queryFn: () => patientsService.search({ q: debouncedSearch, limit: 10 }),
    enabled: debouncedSearch.length >= 2 && !selectedPatientId,
  });

  // Get prescriptions for selected patient (ACTIVE and PARTIALLY_FULFILLED)
  const {
    data: prescriptionsData,
    isLoading: prescriptionsLoading,
    isError: prescriptionsIsError,
    error: prescriptionsError,
  } = useQuery({
    queryKey: ['prescriptions', 'patient', selectedPatientId],
    queryFn: () => prescriptionsService.list({ patientId: selectedPatientId! }),
    enabled: !!selectedPatientId,
  });

  const handleSelectPatient = (patient: { id: string; firstName: string; lastName: string }) => {
    setSelectedPatientId(patient.id);
    setSelectedPatientName(`${patient.lastName} ${patient.firstName}`);
    setSearch('');
  };

  const handleClearPatient = () => {
    setSelectedPatientId(null);
    setSelectedPatientName(null);
    setSearch('');
  };

  const prescriptions = prescriptionsData?.data ?? [];
  const toDeliver = prescriptions.filter(isDeliverable);
  const others = prescriptions.filter((p) => !isDeliverable(p));

  const renderPrescription = (prescription: PrescriptionWithRelations, showExpiry: boolean) => {
    const isMedication = !prescription.documentType || prescription.documentType === 'MEDICATION';
    const items = prescription.items ?? [];
    const names = items
      .map((item) => item.drug?.tradeName ?? item.itemLabel)
      .filter(Boolean)
      .slice(0, 3)
      .join(', ');
    return (
      <li key={prescription.id}>
        <Link
          to={`/pharmacist/prescription/${prescription.id}`}
          className="group flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:border-gray-300 hover:bg-gray-50"
        >
          <div className="min-w-0 flex-1">
            <div className="mb-1.5 flex flex-wrap items-center gap-2">
              <StatusBadge status={prescription.status} />
              {!isMedication && prescription.documentType && (
                <span className="badge-gray">
                  {t(`prescription.docTypesShort.${prescription.documentType.toLowerCase()}`)}
                </span>
              )}
              <span className="text-xs text-gray-500">
                {new Date(prescription.createdAt).toLocaleDateString(dateLocale)}
              </span>
            </div>
            <p className="truncate font-medium text-gray-900">
              {prescription.prescriber
                ? t('pharmacist.common.doctorName', {
                    name: `${prescription.prescriber.lastName} ${prescription.prescriber.firstName}`,
                  })
                : '—'}
            </p>
            <p className="mt-0.5 truncate text-sm text-gray-600">
              {isMedication
                ? t('pharmacist.common.medicationCount', { count: items.length })
                : t('pharmacist.common.orderCount', { count: items.length })}
              {names && (
                <span className="text-gray-500">
                  {' · '}
                  {names}
                  {items.length > 3 && '…'}
                </span>
              )}
            </p>
            {showExpiry && (
              <p className="mt-1 text-xs text-gray-500">
                {t('pharmacist.search.expiresOn', {
                  date: new Date(prescription.expiresAt).toLocaleDateString(dateLocale),
                })}
              </p>
            )}
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-gray-300 transition-colors group-hover:text-gray-500 rtl-flip" />
        </Link>
      </li>
    );
  };

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title={t('pharmacist.search.title')}
        description={t('pharmacist.search.description')}
        actions={
          <Link to="/pharmacist/scan" className="btn-secondary">
            <ScanLine className="h-4 w-4" />
            {t('pharmacist.search.scanQr')}
          </Link>
        }
      />

      {/* Search input */}
      <div className="card mb-6 p-4 sm:p-6">
        <label htmlFor="patient-search" className="label">
          {t('pharmacist.search.patientLabel')}
        </label>

        {selectedPatientId ? (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-primary-200 bg-primary-50 p-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-primary-700">
                <UserRound className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="truncate font-medium text-primary-900">{selectedPatientName}</p>
                <p className="text-sm text-primary-700">{t('pharmacist.search.selected')}</p>
              </div>
            </div>
            <button type="button" onClick={handleClearPatient} className="btn-secondary btn-sm shrink-0">
              {t('pharmacist.search.change')}
            </button>
          </div>
        ) : (
          <div className="relative">
            <Search className="pointer-events-none absolute start-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              id="patient-search"
              type="search"
              autoComplete="off"
              enterKeyHint="search"
              placeholder={t('pharmacist.search.placeholder')}
              className="input py-3 ps-10 text-base sm:text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setSearch('');
              }}
            />

            {/* Patient search results dropdown */}
            {showResults && (
              <div className="absolute z-10 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-card-hover">
                {patientsLoading && !patientResults ? (
                  <div className="flex justify-center p-4">
                    <Spinner />
                  </div>
                ) : patientsError ? (
                  <p className="p-4 text-center text-sm text-danger-700">{t('pharmacist.search.searchError')}</p>
                ) : patientResults?.patients && patientResults.patients.length > 0 ? (
                  <ul className="divide-y divide-gray-100">
                    {patientResults.patients.map((patient) => {
                      const pid = patientIdentifier(patient);
                      return (
                        <li key={patient.id}>
                          <button
                            type="button"
                            className="w-full px-4 py-3 text-start transition-colors hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                            onClick={() => handleSelectPatient(patient)}
                          >
                            <p className="font-medium text-gray-900">
                              {patient.lastName} {patient.firstName}
                            </p>
                            <p className="text-sm text-gray-500">
                              {t(pid.typeKey)}: {pid.masked}
                              {patient.birthYear
                                ? ` · ${t('pharmacist.search.bornIn', { year: patient.birthYear })}`
                                : ''}
                            </p>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="p-4 text-center text-sm text-gray-500">{t('pharmacist.search.noPatient')}</p>
                )}
              </div>
            )}
          </div>
        )}

        {!selectedPatientId && <p className="field-hint">{t('pharmacist.search.hint')}</p>}
      </div>

      {/* Prescriptions list */}
      {selectedPatientId && (
        <div className="card p-4 sm:p-6">
          <h2 className="card-title mb-4">{t('pharmacist.search.patientPrescriptions')}</h2>

          {prescriptionsLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-6 w-6" />
            </div>
          ) : prescriptionsIsError ? (
            <div className="alert-danger">{getApiError(prescriptionsError).message}</div>
          ) : prescriptions.length > 0 ? (
            <div className="space-y-6">
              {toDeliver.length > 0 && (
                <section>
                  <h3 className="section-title mb-3">{t('pharmacist.search.toDispense')}</h3>
                  <ul className="space-y-3">{toDeliver.map((p) => renderPrescription(p, true))}</ul>
                </section>
              )}

              {others.length > 0 && (
                <section>
                  <h3 className="section-title mb-3">{t('pharmacist.search.alreadyDispensed')}</h3>
                  <ul className="space-y-3">{others.map((p) => renderPrescription(p, false))}</ul>
                </section>
              )}
            </div>
          ) : (
            <EmptyState icon={FileText} title={t('pharmacist.search.noPrescriptions')} />
          )}
        </div>
      )}

      {selectedPatientId && (
        <div className="mt-4 text-center">
          <button type="button" onClick={handleClearPatient} className="btn-ghost">
            <X className="h-4 w-4" />
            {t('pharmacist.search.newSearch')}
          </button>
        </div>
      )}
    </div>
  );
}
